import { useProject } from '@contexts/Project.context';
import { PROJECT_MODE } from '@utils/constants';

import { MAP_CONTROLS_LAYOUT } from './constants';
import { AsideSlot, ClusterContainer, ControlRail } from './StyledMapControls';

/**
 * MapControlsCluster — owns the bottom-right corner of the map viewport.
 *
 * Controls used to position themselves individually, each repeating the same
 * hand-computed offsets. This component replaces that with one anchored flex
 * container so controls can be added or reordered without touching any
 * coordinates:
 *
 *     [ aside ]  [ rail (bottom-anchored, grows upward) ]
 *
 * `aside` is for passive readouts that read better laterally (the scale bar).
 * `children` form the vertical rail; JSX order is top-to-bottom on screen, so
 * the last child sits closest to the corner.
 *
 * The cluster stays mounted when hidden. Several controls own OpenLayers
 * resources in their effects (the base map tile layer, for one), so unmounting
 * them would tear those down — hiding must not have side effects.
 *
 * @param {Object} props
 * @param {React.ReactNode} [props.aside] Readout rendered left of the rail, bottom-aligned.
 * @param {boolean} [props.isVisible] Whether the cluster is shown. Hidden, not unmounted.
 * @param {React.ReactNode} props.children Controls stacked in the rail, top to bottom.
 */
const MapControlsCluster = ({ aside = null, isVisible = true, children }) => {
	const { projectMode, singleImageSliderOpen, singleImageFeatures } = useProject();

	// The single-image carousel spans the full viewport width, so the cluster
	// lifts above it. This is the only place that offset is decided; every
	// control moves together because they share this container.
	const isCarouselVisible =
		projectMode === PROJECT_MODE.SINGLE_IMAGE &&
		singleImageSliderOpen &&
		singleImageFeatures?.length > 0;

	const bottom = isCarouselVisible
		? MAP_CONTROLS_LAYOUT.bottomWithCarousel
		: MAP_CONTROLS_LAYOUT.bottom;

	return (
		<ClusterContainer $bottom={bottom} $hidden={!isVisible}>
			{/*
			 * Unmounted while hidden, unlike the rail. The keep-mounted rule exists
			 * for controls that own OpenLayers resources; a readout owns none, and
			 * the scale bar recomputes on every `change:resolution` — once per frame
			 * of a zoom — which behind `display: none` is pure waste.
			 */}
			{isVisible && aside && <AsideSlot>{aside}</AsideSlot>}

			<ControlRail>{children}</ControlRail>
		</ClusterContainer>
	);
};

export default MapControlsCluster;
